import { createRouter as _createRouter, createWebHistory, type RouteRecordRaw } from "vue-router";
import { useThemeContext } from "@/core/composables";
import { globals } from "@/core/globals";
import { resolveLocalizedRedirect, withLocalizedAliases } from "@/router/routes/localization";
import { buildRedirectUrl, getReturnUrlValue } from "@/core/utilities";
import { ROUTES } from "@/router/routes/constants";
import { useUser } from "@/shared/account";
import { mainRoutes } from "./routes";
import type { RouteRecordName } from "vue-router";

export function createRouter(options: { base: string }) {
  const { base } = options;
  const { isAuthenticated, isAdministrator, operator, organization } = useUser();
  const { themeContext } = useThemeContext();

  // Innovadis custom: helper -- take fixed header height in account for better positioning
  function getHeaderOffset(): number {
    const header = document.querySelector(".inno-header") as HTMLElement | null;
    let offSet = header?.offsetHeight ?? 0;

    // add a bit extra for better positioning
    if (offSet > 0) {
      offSet += 40;
    }

    return offSet;
  }

  // Innovadis custom: helper
  function findTarget(hash: string): HTMLElement | null {
    if (!hash || hash === "#") return null;
    const sel = decodeURIComponent(hash);
    return (document.querySelector(sel) as HTMLElement | null) || document.getElementById(sel.slice(1));
  }

  // Innovadis custom: helper -- if scroll does not work on load, perhaps the page is too slow for 1500ms
  function waitForEl(hash: string, timeoutMs = 1500): Promise<HTMLElement | null> {
    const start = performance.now();
    return new Promise((resolve) => {
      const tick = () => {
        const el = findTarget(hash);
        if (el) return resolve(el);
        if (performance.now() - start >= timeoutMs) return resolve(null);
        requestAnimationFrame(tick);
      };
      tick();
    });
  }

  const router = _createRouter({
    // Innovadis custom add locale
    routes: [
      {
        path: "/:locale?",
        children: withLocalizedAliases(mainRoutes),
      },
    ],
    history: createWebHistory(base),

    // Innovadis custom: improved scroll behavior on page load when having an anchor link in the url
    async scrollBehavior(to, from, savedPosition) {
      // 1) browser back/forward
      if (savedPosition) return savedPosition;

      // 2) hash navigation (also covers initial page load with #hash)
      if (to.hash) {
        const el = await waitForEl(to.hash);
        if (el) {
          const y = el.getBoundingClientRect().top + window.scrollY - getHeaderOffset();
          // smooth when staying on same path; instant on first load / route change
          const behavior: ScrollBehavior = to.path === from.path ? "smooth" : "auto";
          return { left: 0, top: y, behavior };
        }
        // if target never appears, fall through to default
      }

      // 3) on path change, go to top; otherwise keep current scroll
      if (to.path !== from.path) return { top: 0 };
      return undefined;
    },
  });

  router.beforeEach((to, _from, next) => {
    // Normalize generic paths to current-locale paths (e.g., "/cart" -> "/winkelwagen") while preserving the visible URL
    // Only rewrite when navigating via named routes or plain paths that match a localized mapping
    const twoLetterFromCulture = globals.cultureName?.slice(0, 2);
    const currentLocale = twoLetterFromCulture ?? themeContext.value.defaultLanguage.twoLetterLanguageName;

    const redirect = resolveLocalizedRedirect(to, currentLocale);
    if (redirect) {
      return next(redirect);
    }

    // Protecting routes
    const unauthorizedAccessIsDenied: boolean =
      !isAuthenticated.value &&
      !to.meta.public &&
      (to.meta.requiresAuth || !themeContext.value.storeSettings.anonymousUsersAllowed);

    if (unauthorizedAccessIsDenied) {
      // save current location to return to it after sign in
      const query = buildRedirectUrl(to) || {};

      return next({
        name: ROUTES.SIGN_IN.NAME,
        query,
      });
    }

    // Protecting company routes
    if (to.meta.requiresOrganization && !organization.value) {
      return next({ name: "Account" });
    }

    // Make Dashboard the default Home page for authorized users
    if (isAdministrator.value && !operator.value && Array<RouteRecordName>("SignIn").includes(to.name!)) {
      return next({ name: "ImpersonateUserDashboard" });
    } else if (
      isAuthenticated.value &&
      Array<RouteRecordName>(
        "Home",
        ROUTES.SIGN_IN.NAME,
        "SignUp",
        "ForgotPassword",
        "ResetPassword",
        "SetPassword",
        "ConfirmInvitation",
      ).includes(to.name)
    ) {
      return next(getReturnUrlValue() || { name: ROUTES.CATALOG.NAME });
    }

    return next();
  });

  // Innovadis custom
  // Remove the hash from the URL after we’ve scrolled to it.
  // Uses a delay to ensure scrollBehavior has run.
  router.afterEach((to) => {
    if (!to.hash) return;
    setTimeout(() => {
      const cleanHref = router.resolve({ path: to.path, query: to.query, hash: undefined }).href;
      history.replaceState(history.state, "", cleanHref);
    }, 2500);
  });

  return router;
}
